// hooks/useActiveFiltersUI.js
import { useCallback } from 'react';

export const useActiveFiltersUI = ({
  selectedFilters,
  loadingFilterOptions,
  filterOptionsLoaded,
  fetchFilterOptions,
  getFilterDisplayValue,
  removeFilter,
  clearAllFilters,
  activeFilterCount,
  workspaces,
  projects,
  jobs,
}) => {
  const getFilterButtonLabel = useCallback((filterType) => {
    const count = selectedFilters[filterType]?.length || 0;
    const isLoading = loadingFilterOptions[filterType];
    const labels = {
      date: "Date",
      workspace: "Workspace",
      project: "Project",
      job: "Job",
      userName: "User",
      task: "Task",
    };
    const icons = {
      date: "calendar",
      workspace: "building",
      project: "project-diagram",
      job: "tasks",
      userName: "user",
      task: "list-check",
    };
    const hasOther = Object.entries(selectedFilters).some(
      ([k, v]) => k !== filterType && v.length > 0,
    );
    return (
      <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {isLoading ? (
          <i
            className="fas fa-spinner fa-spin"
            style={{ fontSize: "14px", color: "#3498db" }}
          />
        ) : (
          <i
            className={`fas fa-${icons[filterType]}`}
            style={{
              fontSize: "14px",
              color: hasOther ? "#17a2b8" : "#3498db",
            }}
          />
        )}
        {isLoading ? `Loading ${labels[filterType]}...` : labels[filterType]}
        {count > 0 && ` (${count})`}
        {hasOther && !isLoading && (
          <span title="Filtered by other selections">
            <i className="fas fa-filter" style={{ color: "#17a2b8" }} />
          </span>
        )}
      </span>
    );
  }, [selectedFilters, loadingFilterOptions]);

  const ensureFilterOptionsLoaded = useCallback(
    async (filterType) => {
      if (!filterOptionsLoaded[filterType] && !loadingFilterOptions[filterType])
        await fetchFilterOptions(filterType);
    },
    [filterOptionsLoaded, loadingFilterOptions, fetchFilterOptions],
  );

  const renderActiveFilters = useCallback(() => {
    const activeFilters = [];
    Object.entries(selectedFilters).forEach(([type, values]) => {
      values.forEach((value) => {
        if (!filterOptionsLoaded[type] && !loadingFilterOptions[type])
          ensureFilterOptionsLoaded(type);
        const display = getFilterDisplayValue(
          type,
          value,
          workspaces,
          projects,
          jobs,
        );
        activeFilters.push({ filterType: type, value, displayValue: display });
      });
    });
    if (!activeFilters.length) return null;
    return (
      <div className="active-filters-container">
        <div className="active-filters-title">
          <i className="fas fa-filter" /> Active Filters ({activeFilterCount}):
        </div>
        <div className="active-filters-content">
          {activeFilters.map((f, i) => (
            <div key={`${f.filterType}-${f.value}-${i}`} className="filter-tag">
              <span>
                {f.filterType === "date"
                  ? "Date"
                  : f.filterType === "workspace"
                    ? "Workspace"
                    : f.filterType === "project"
                      ? "Project"
                      : f.filterType === "job"
                        ? "Job"
                        : "User"}
                : {f.displayValue}
              </span>
              <button
                onClick={() => removeFilter(f.filterType, f.value)}
                className="filter-tag-remove"
              >
                <i className="fas fa-times" />
              </button>
            </div>
          ))}
          <button onClick={clearAllFilters} className="clear-all-button">
            <i className="fas fa-times-circle" /> Clear All
          </button>
        </div>
      </div>
    );
  }, [
    selectedFilters,
    filterOptionsLoaded,
    loadingFilterOptions,
    ensureFilterOptionsLoaded,
    getFilterDisplayValue,
    workspaces,
    projects,
    jobs,
    activeFilterCount,
    removeFilter,
    clearAllFilters,
  ]);

  return {
    getFilterButtonLabel,
    renderActiveFilters,
    ensureFilterOptionsLoaded,
  };
};